import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Target, Layers, MessageCircle, BookOpen, Zap, Image, Mic, Move } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";
import { api, storage } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const AREA_ICONS = {
  1: Target,
  2: Layers,
  3: MessageCircle,
  4: BookOpen,
  5: Zap,
  6: Image,
  7: Mic,
  8: Move,
};

const AREA_COLORS = [
  "#00E5FF", "#7C4DFF", "#00E676", "#FF6D00",
  "#FF1744", "#2979FF", "#FFEA00", "#F500FF"
];

const SAMPLE_SCORES = [7.5, 6.8, 5.3, 7.2, 4.8, 8.0, 5.5, 6.2];

export default function Landing() {
  const navigate = useNavigate();
  const { lang, lp, t } = useLang();
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    api.getSeedData(lang).then(data => {
      setAreas(data.areas);
    }).catch(console.error);
  }, [lang]);

  const chartData = areas.length
    ? areas.map((a, i) => ({ subject: a.name, score: SAMPLE_SCORES[i], fullMark: 10 }))
    : [];

  const handleStart = () => {
    const user = storage.getUser();
    if (user) {
      const progress = storage.getAssessmentProgress();
      if (progress && progress.userId === user.id) {
        navigate(lp(`/assessment/${progress.currentStep}`));
      } else {
        navigate(lp("/assessment/1"));
      }
    } else {
      navigate(lp("/profile"));
    }
  };

  const handleSampleReport = () => {
    navigate(lp("/results/sample"));
  };

  return (
    <div className="min-h-screen bg-[#222222] text-white">
      {/* Header */}
      <header className="px-6 py-4 flex justify-between items-center max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#FFEA00] flex items-center justify-center">
            <Target className="w-5 h-5 text-black" />
          </div>
          <span className="font-semibold text-lg hidden sm:block">{t.landing.brand}</span>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-sm text-white/60 hidden sm:block">{t.branding}</p>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t.landing.heroLine1}<br />
              <span className="text-[#FFEA00]">{t.landing.heroHighlight}</span>
            </h1>
            <p className="text-lg text-white/70 mb-8 max-w-lg">
              {t.landing.heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="start-assessment-btn"
                onClick={handleStart}
                className="bg-[#FFEA00] hover:bg-[#FFD600] text-black font-semibold px-8 py-6 text-lg rounded-lg transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#FFEA00]/20"
              >
                {t.landing.startAssessment}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                data-testid="sample-report-btn"
                variant="outline"
                onClick={handleSampleReport}
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg"
              >
                {t.landing.seeSample}
              </Button>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative hidden md:block"
          >
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img
                src={lang === "ru"
                  ? "/ru-speaking-wheel.svg"
                  : "https://customer-assets.emergentagent.com/job_speakingwheel/artifacts/wp7bm9ho_Jak%20mo%CC%81wic%CC%81%2C%20aby%20s%C5%82uchali%20-%20GCF%20%28Instagram%20Post%29%20%283%29.png"}
                alt={t.landing.heroHighlight}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#222222] via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* 8 Areas Grid */}
      <section className="px-6 py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t.landing.areasTitle}
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              {t.landing.areasSubtitle}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {areas.map((area, index) => {
              const Icon = AREA_ICONS[area.id] || Target;
              return (
                <motion.div
                  key={area.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-xl bg-[#2a2a2a] border border-white/5 hover:border-white/20 transition-all group cursor-pointer"
                  style={{ '--area-color': area.color }}
                >
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${area.color}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: area.color }} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{area.name}</h3>
                  <p className="text-sm text-white/50 line-clamp-2">{area.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-6 py-16 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t.landing.howItWorks}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {t.landing.steps.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-full bg-[#FFEA00] text-black font-bold text-2xl flex items-center justify-center mx-auto mb-4">
                {index + 1}
              </div>
              <h3 className="font-semibold text-xl mb-2">{item.title}</h3>
              <p className="text-white/60">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Sample Report Preview */}
      <section className="px-6 py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t.landing.wheelTitle}
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              {t.landing.wheelSubtitle}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Wheel Chart */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-[#2a2a2a] rounded-2xl p-6 border border-white/5"
            >
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                  <PolarGrid stroke="rgba(255,255,255,0.1)" />
                  <PolarAngleAxis
                    dataKey="subject"
                    tick={{ fill: '#fff', fontSize: 11 }}
                    tickLine={false}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 10]}
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                    axisLine={false}
                  />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#FFEA00"
                    fill="#FFEA00"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
              <p className="text-center text-white/40 text-sm mt-2">{t.landing.sampleResult}</p>
            </motion.div>

            {/* Sample Scores */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="bg-[#2a2a2a] rounded-xl p-6 border border-white/5">
                <div className="text-center mb-6">
                  <p className="text-white/60 text-sm mb-1">{t.landing.sampleOverall}</p>
                  <p className="text-5xl font-bold text-[#FFEA00]">51.3<span className="text-xl text-white/40">/80</span></p>
                  <p className="text-2xl text-white mt-1">64%</p>
                </div>

                <div className="space-y-3">
                  {chartData.map((item, idx) => (
                    <div key={item.subject} className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: AREA_COLORS[idx] }}
                      />
                      <span className="flex-1 text-sm text-white/70">{item.subject}</span>
                      <span
                        className="font-semibold text-sm"
                        style={{ color: AREA_COLORS[idx] }}
                      >
                        {item.score}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={handleSampleReport}
                variant="outline"
                className="w-full border-white/30 text-white hover:bg-white/10 py-6"
                data-testid="view-sample-report-btn"
              >
                {t.landing.viewFullSample}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-16 bg-[#1a1a1a]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center"
        >
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t.landing.ctaTitle}
          </h2>
          <p className="text-white/60 mb-8">
            {t.landing.ctaSubtitle}
          </p>
          <Button
            data-testid="start-assessment-btn-bottom"
            onClick={handleStart}
            className="bg-[#FFEA00] hover:bg-[#FFD600] text-black font-semibold px-8 py-6 text-lg rounded-lg transition-all hover:scale-105"
          >
            {t.landing.startYourAssessment}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">{t.landing.footerCopy}</p>
          <p className="text-white/40 text-sm">{t.branding}</p>
        </div>
      </footer>
    </div>
  );
}
